import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { skipToken, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useConfig } from 'wagmi'

import { TopbarRewardsProps } from '../components/topbar-rewards/TopbarRewards'
import { getChainConfigEntry } from '@/config/chain'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import {
  aaveDataLayer,
  aaveDataLayerQueryKey,
} from '@/features/sparklend/common/data/market-info/aave-data-layer/query'
import { marketInfoSelectFn } from '@/features/sparklend/common/data/market-info/marketInfo'
import { claimRewardsDialogConfig } from '@/features/sparklend/dialogs/claim-rewards/ClaimRewardsDialog'

interface UseRewardsInfoParams {
  chainId: number
  address: CheckedAddress | undefined
}

export function useRewardsInfo({ chainId, address }: UseRewardsInfoParams): TopbarRewardsProps {
  const wagmiConfig = useConfig()
  const { sparkLend } = getChainConfigEntry(chainId)

  const marketInfo = useQuery({
    queryKey: aaveDataLayerQueryKey({ chainId, account: address }),
    queryFn: sparkLend ? aaveDataLayer({ wagmiConfig, account: address, chainId }).queryFn : skipToken,
    select: useMemo(() => marketInfoSelectFn({}), []),
  })

  const openDialog = useOpenDialog()

  const rewards = (marketInfo.data?.userRewards ?? []).map((reward) => ({
    token: reward.token,
    amount: reward.value,
  }))

  const totalClaimableReward = rewards.reduce(
    (acc, { token, amount }) => acc.plus(token.toUSD(amount)),
    NormalizedNumber.ZERO,
  )

  return {
    rewards,
    totalClaimableReward,
    onClaim: () => {
      openDialog(claimRewardsDialogConfig, {})
    },
  }
}
